import type {EntityPatch} from '../history/types';
import type {Viewer} from '../viewer/Viewer';
import type {SketchDrawingManager} from '../manager/SketchDrawingManager';
import {SketchDrawingTool} from './DrawingSharedTypes';
import {LineMaterialFactory, LineMaterialState, PointsMaterialFactory, type SketchLineMaterial,} from '../material';
import {SketchCollectionObject, SketchPointKind, SketchPointObject,} from '../mesh/sketch';
import type {CollectSource} from '../mesh/sketch/ISketchObject';
import * as THREE from "three/webgpu";
import {addObject3DToParent} from '../utils/scene-object/addObject3DToParent';
import {SKETCH_GEOMETRY_TOLERANCE_CONFIG} from '../sketch/config/SketchGeometryToleranceConfig';
import type {PointSnapResult} from '../manager/pointSnap/PointSnapTypes';
import {detectPolylineSelfIntersection, type PolylinePointLike} from '../tool/detectPolylineSelfIntersection';


/** 草图线颜色（用于实体着色）。 */
const SKETCH_LINE_COLOR = 0x53a9ff;

/** 草图预览颜色（所有处理器共用）。 */
export const SKETCH_PREVIEW_COLOR = 0x53a9ff;


/**
 * 绘制鼠标按下事件载荷。
 */
export interface SketchDrawingMouseDownPayload {
    /** 浏览器原始鼠标事件。 */
    event: MouseEvent;
    /** 捕捉后的世界坐标；有捕捉结果时由 SketchDrawingManager 注入，优先级高于 event 坐标。 */
    snappedPoint?: THREE.Vector3 | null;
    /** 捕捉命中的草图点对象；仅点类捕捉命中时由 SketchDrawingManager 注入。 */
    snappedTargetPoint?: SketchPointObject | null;
    /**
     * 单击瞬间接受的完整捕捉结果。
     * 说明：绘制处理器只能消费该结果携带的业务语义，禁止根据最终坐标事后猜测捕捉类型。
     */
    snapResult?: PointSnapResult | null;
}

/**
 * 草图绘制处理器抽象基类。
 * 说明：
 * 1. 每个处理器只负责一个工具类型；
 * 2. 所有处理器通过统一入口被绘制管理器调度；
 * 3. 渲染器通过管理器懒加载获取，避免构造时序问题；
 * 4. 提供绘制次数、是否可闭合、预览创建/销毁等公共抽象。
 */
export abstract class SketchDrawingHandler {
    /** 本次绘制积累的领域补丁（随绘制事务一同提交）。 */
    protected pendingHistoryPatches: EntityPatch[] = [];

    /** 处理器对应的工具类型。 */
    abstract readonly tool: SketchDrawingTool | readonly SketchDrawingTool[];

    /** 草图绘制管理器实例（所有依赖的来源）。 */
    protected readonly sketchDrawingManager: SketchDrawingManager;

    /** 绘制点集合（内部化，各子类共享）。 */
    drawingPoints: THREE.Vector3[] = [];

    /** 预览自相交检测复用的参考面局部坐标缓冲，避免每个采样点重复分配。 */
    private readonly previewSelfIntersectionLocalBuffer = new THREE.Vector2();

    /**
     * 构造函数。
     * @param sketchDrawingManager 草图绘制管理器实例。
     */
    protected constructor(sketchDrawingManager: SketchDrawingManager) {
        this.sketchDrawingManager = sketchDrawingManager;
    }


    /**
     * 获取当前模式下完成一次绘制所需的点击次数。
     * 说明：
     * 1. 固定步骤工具（圆、矩形、椭圆等）返回具体数值；
     * 2. 多点绘制工具（样条、多段线等）返回 Infinity。
     * @returns 所需点数。
     */
    abstract getRequiredPointCount(): number;


    /**
     * 当前工具是否支持闭合。
     * 说明：
     * 1. 支持闭合的工具（线链、多段线、样条等）返回 true；
     * 2. 不支持闭合的工具（点、圆、矩形等）返回 false。
     */
    abstract get closable(): boolean;


    /**
     * 获取当前绘制提示文本。
     * @param activeTool 当前激活的工具类型（多工具处理器使用）。
     * @returns 提示文本。
     */
    abstract getDrawingHint(activeTool?: SketchDrawingTool): string;


    /**
     * 重置处理器内部状态。
     * 说明：
     * 1. 由管理器在 startDrawing / stopDrawing 时统一调用；
     * 2. 各子类负责清理自己的参数、链路状态等。
     */
    abstract resetState(): void;

    /**
     * 撤销当前命令内尚未提交的最后一个输入点。
     *
     * 说明：这是草图绘制命令内 undo 的统一入口，只处理还没有进入模型历史的临时输入。
     * 已经提交成草图实体的几何必须交给全局模型 undo，否则 Ctrl+Z 会被绘制态误消费。
     *
     * @returns 是否真实撤销了一个未提交输入点。
     */
    public undoLastDrawingPoint(): boolean {
        if (this.drawingPoints.length === 0) {
            return false;
        }
        this.drawingPoints.pop();
        return true;
    }


    /**
     * 处理器被激活时的钩子。
     * 说明：
     * 1. 由管理器在 startDrawing 时调用；
     * 2. 默认空操作，子类按需覆写以执行激活初始化。
     * @param _tool 当前激活的工具类型。
     */
    onActivate(_tool: SketchDrawingTool): void {
        // 默认空操作。
    }

    /**
     * 获取当前绘制引用平面。
     *
     * @returns 当前 Viewer 的绘制引用平面。
     * @throws 当 Viewer 尚未设置引用平面时抛出错误。
     */
    get referencePlane() {
        if (!this.viewer.currentReferencePlane){
            throw new Error()
        }
        return this.viewer.currentReferencePlane
    }
    /**
     * 获取绘图点（3D空间坐标）
     */
    getDrawingPoint(event: MouseEvent){
        return this.sketchDrawingManager.getDrawingPoint(event);
    }
    /**
     * 通过管理器懒加载获取 Viewer 实例。
     * 说明：渲染器在 configureDispatch 之后才可用，通过 getter 延迟解析。
     */
    protected get viewer(): Viewer {
        return this.sketchDrawingManager.viewer;
    }

    /**
     * 处理鼠标按下事件。
     * @param payload 鼠标按下载荷。
     */
    abstract handleMouseDown(
        payload: SketchDrawingMouseDownPayload,
    ): void;


    /**
     * 处理鼠标双击事件。
     * 说明：默认不处理双击，连续绘制类工具可按需覆写，避免把 mouseDown.detail 误判为完成命令。
     * @param _payload 鼠标双击载荷。
     */
    handleDoubleClick(_payload: SketchDrawingMouseDownPayload): void {
        // 默认空操作。
    }


    /**
     * 处理预览更新。
     * @param currentPoint 当前预览点。
     * @param currentVersion 预览版本号。
     * @param tool 当前工具类型。
     * @param startPointOverride
     */
    updatePreview?(
        currentPoint: THREE.Vector3,
        currentVersion: number,
        tool?: SketchDrawingTool,
        startPointOverride?: THREE.Vector3,
    ): void | Promise<void>;

    /**
     * 获取当前绘制流程中的临时点列表。
     * @returns 绘制点数组。
     */
    protected getDrawingPoints(): THREE.Vector3[] {
        return this.drawingPoints;
    }


    /**
     * 创建线段预览。
     * 说明：多个处理器共用的线段预览创建逻辑，统一在基类实现。
     * @param start 起点世界坐标。
     * @param end 终点世界坐标。
     * @param color 预览颜色（默认使用统一预览色）。
     */
    protected createLinePreview(
        start: THREE.Vector3,
        end: THREE.Vector3,
        color: number = SKETCH_PREVIEW_COLOR,
    ): void {
        // 清理旧预览。
        this.destroyPreview();
        const geometry = new THREE.BufferGeometry().setFromPoints([start, end]);
        const material = new THREE.LineBasicMaterial({
            color,
            linewidth: 2,
            opacity: 0.8,
            transparent: true,
        });
        this.sketchDrawingManager.previewLine = new THREE.Line(geometry, material);
        this.viewer.addObject(this.sketchDrawingManager.previewLine);
    }

    /**
     * 创建点集预览。
     * 说明：在预览中渲染临时控制点。
     * @param points 控制点世界坐标列表。
     * @param color 预览颜色（默认使用统一预览色）。
     */
    protected createPointPreview(
        points: THREE.Vector3[],
        color: number = SKETCH_PREVIEW_COLOR,
    ): void {

        // 清理旧预览点。
        if (this.sketchDrawingManager.previewPoints) {
            this.viewer.scene.remove(this.sketchDrawingManager.previewPoints);
            this.disposePreviewPointGroup(this.sketchDrawingManager.previewPoints);
            this.sketchDrawingManager.previewPoints = null;
        }
        if (points.length === 0) {
            return;
        }

        const previewMaterial = PointsMaterialFactory.getSketchControlPointSpriteMaterial().clone();
        previewMaterial.color.setHex(color);
        const previewPointGroup = new THREE.Group();
        previewPointGroup.name = '草图绘制-预览点组';
        previewPointGroup.userData.previewPointMaterial = previewMaterial;

        points.forEach((point) => {
            const previewPointSprite = new THREE.Sprite(previewMaterial);
            previewPointSprite.position.copy(point);
            previewPointSprite.scale.set(0.009, 0.009, 0.009);
        addObject3DToParent(previewPointSprite, previewPointGroup);
        });

        this.sketchDrawingManager.previewPoints = previewPointGroup;
        this.viewer.addObject(this.sketchDrawingManager.previewPoints);

    }


    /**
     * 释放预览点组资源。
     * 说明：
     * 1. 预览点组内所有 Sprite 共享同一个克隆材质；
     * 2. 只释放一次材质，避免重复 dispose；
     * 3. Sprite 使用共享几何，不做几何销毁。
     * @param previewPointGroup 预览点组。
     */
    protected disposePreviewPointGroup(previewPointGroup: THREE.Group): void {
        const previewPointMaterial = previewPointGroup.userData.previewPointMaterial;
        if (previewPointMaterial instanceof THREE.Material) {
            previewPointMaterial.dispose();
        }
        previewPointGroup.clear();
    }


    /**
     * 销毁当前预览。
     * 说明：统一清除绘制预览对象，避免各子类分散调用。
     */
    protected destroyPreview(): void {
        this.sketchDrawingManager.clearDrawingPreview();
    }


    /**
     * 统一清理绘制交互状态。
     * @param options 清理选项。
     */
    protected cleanupDrawingState(options?: {
        clearDrawingPoints?: boolean;
        clearDrawingPreview?: boolean;
    }): void {
        const resolvedOptions = {
            clearDrawingPoints: true,
            clearDrawingPreview: true,
            ...options,
        };
        if (resolvedOptions.clearDrawingPoints) {
            this.drawingPoints = [];
        }
        if (resolvedOptions.clearDrawingPreview) {
            this.destroyPreview();
        }
    }


    /**
     * 判断是否允许吸附到闭合起点。
     * 说明：仅在已有足够点数时允许闭合吸附。
     * @returns 是否允许。
     */
    protected canSnapToClosureStart(): boolean {
        return this.closable && this.drawingPoints.length >= 2;
    }

    /**
     * 获取闭合吸附链起点。
     * @returns 起点坐标；不存在返回 null。
     */
    protected resolveChainStartPointForClosure(): THREE.Vector3 | null {
        const points = this.drawingPoints;
        return points.length >= 1 ? points[0].clone() : null;
    }

    /**
     * 获取当前线段起点（用于闭合检测）。
     * @returns 起点坐标；不存在返回 null。
     */
    protected resolveSegmentStartPointForClosure(): THREE.Vector3 | null {
        const points = this.drawingPoints;
        return points.length >= 1 ? points[points.length - 1].clone() : null;
    }

    /**
     * 判断给定点是否在闭合容差内。
     * @param point 待检测点。
     * @param target 目标点。
     * @returns 是否在容差内。
     */
    protected isWithinClosureTolerance(point: THREE.Vector3, target: THREE.Vector3): boolean {
        return point.distanceTo(target) <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.drawingClosureSnap;
    }

    //------ 预览自相交检测平面投影 开始------
    /**
     * 在草图参考面局部 2D 坐标系内检测折线自相交。
     *
     * 设计说明：
     * 1. 曲线采样点以世界坐标给出，而自相交判定本质是草图平面内的二维判定；
     * 2. {@link detectPolylineSelfIntersection} 只读取 x/y（假定草图位于世界 XY 平面），
     *    直接把世界点传进去等价于把曲线正交投影到世界 XY 平面：仅前视这类与世界 XY 平面
     *    平行的草图面成立；用户创建基准面、上视、右视等非平行面会在该投影下退化
     *    （法向 Z 分量趋近 0 时整条曲线被压成一条线，非相邻边转为共线重叠）；
     * 3. 退化投影会把“回画”路径误判为自相交，预览被整块冻结，表现为只能沿一个方向向前
     *    绘制、无法回画闭合；
     * 4. 因此统一先由参考面把世界采样点正交投影到平面局部坐标再检测：参考面轴向为单位
     *    正交向量，平面内距离与角度与世界坐标等价，检测语义不随基准面朝向改变；
     * 5. 参考面缺失时按“未自交”返回：预览属于可丢弃派生状态，不能因缺少投影上下文而冻结。
     *
     * @param curvePoints 曲线世界坐标采样点列。
     * @param options 检测选项（闭合状态）。
     * @returns 折线在草图平面内是否自相交。
     */
    protected isPolylineSelfIntersectingInPlane(
        curvePoints: readonly THREE.Vector3[],
        options?: { closed?: boolean },
    ): boolean {
        const referencePlane = this.viewer.currentReferencePlane;
        if (!referencePlane) {
            return false;
        }

        const localPoints: PolylinePointLike[] = curvePoints.map((curvePoint) => {
            const localPoint = referencePlane.worldToLocalTo(
                curvePoint,
                this.previewSelfIntersectionLocalBuffer,
            );
            return {x: localPoint.x, y: localPoint.y, z: 0};
        });

        return detectPolylineSelfIntersection(localPoints, options).isSelfIntersecting;
    }
    //------ 预览自相交检测平面投影 结束------


    /**
     * 解析可写入新建控制点的捕捉来源点。
     * 说明：
     * 1. 该方法只返回来源引用，不表示复用同一个点对象；
     * 2. 捕捉点必须与最终控制点位置仍在闭合容差内，避免绘制模式投影后错误记录来源；
     * 3. 来源点类型不受限制，端点、中点、中心点、象限点都可作为来源。
     * @param targetPoint 捕捉命中的点对象。
     * @param expectedPoint 新建控制点最终位置。
     * @returns 可记录的来源点；不可记录时返回 null。
     */
    protected resolveControlPointSource(
        targetPoint: SketchPointObject | null | undefined,
        expectedPoint: THREE.Vector3 | null | undefined,
    ): SketchPointObject | null {
        if (!targetPoint || !expectedPoint) {
            return null;
        }
        return this.isWithinClosureTolerance(targetPoint.getPosition(), expectedPoint)
            ? targetPoint
            : null;
    }

    /**
     * 解析与实体控制点位置一一对应的可复用捕捉目标列表。
     * 说明：
     * 1. 采用“位置匹配”而非索引对齐：样条插值归一化会压缩连续重合点，
     *    索引对齐会在压缩发生后错位，位置匹配天然兼容压缩与闭合截断；
     * 2. 同一捕捉目标对象最多复用一次，避免同一个点对象在控制点列表中重复出现；
     * 3. 匹配判定复用 `resolveControlPointSource` 的闭合容差规则。
     * @param controlPointPositions 实体化后的控制点位置列表。
     * @param drawingPointTargets 绘制点击序列对应的捕捉目标列表。
     * @returns 与位置列表等长的可复用目标列表；未命中时对应项为 null。
     */
    protected resolveReusableControlPointTargets(
        controlPointPositions: readonly THREE.Vector3[],
        drawingPointTargets: readonly (SketchPointObject | null)[],
    ): Array<SketchPointObject | null> {
        const targets: Array<SketchPointObject | null> = [];
        const usedTargetIds = new Set<string>();
        for (const position of controlPointPositions) {
            let matched: SketchPointObject | null = null;
            for (const candidate of drawingPointTargets) {
                // 已复用过的目标不允许再次复用，防止同一对象重复进入控制点列表。
                if (!candidate || usedTargetIds.has(candidate.uuid)) {
                    continue;
                }
                if (this.resolveControlPointSource(candidate, position)) {
                    matched = candidate;
                    usedTargetIds.add(candidate.uuid);
                    break;
                }
            }
            targets.push(matched);
        }
        return targets;
    }

    /**
     * 解析可直接复用为曲线控制点的已有端点。
     * 说明：
     * 1. 允许复用任意草图边类型（直线、圆弧、椭圆、样条等）的端点控制点，
     *    确保不同边类型之间可以共享端点对象，使选择链的拓扑遍历正常工作；
     * 2. 点位必须与最终控制点位置仍在闭合容差内；
     * 3. 仅复用已标记为端点角色（isEndPoint）的草图点。
     * @param targetPoint 捕捉命中的点对象。
     * @param expectedPoint 最终控制点位置。
     * @returns 可复用的端点对象；否则返回 null。
     */
    protected resolveReusableLineEndpointControlPoint(
        targetPoint: SketchPointObject | null | undefined,
        expectedPoint: THREE.Vector3 | null | undefined,
    ): SketchPointObject | null {
        const reusablePoint = this.resolveControlPointSource(targetPoint, expectedPoint);
        if (!reusablePoint?.isEndPoint) {
            return null;
        }
        // 任意边类型的端点均可复用，不再限制为仅直线端点
        return reusablePoint;
    }

    /**
     * 为已创建的控制点绑定捕捉来源点。
     * @param point 新建控制点。
     * @param sourcePoint 捕捉来源点。
     */
    protected bindControlPointSource(
        point: SketchPointObject,
        sourcePoint: SketchPointObject | null | undefined,
    ): void {
        if (!sourcePoint) {
            return;
        }
        point.bindSourcePoint(sourcePoint);
    }


    /**
     * 创建草图控制点实体。
     * @param position 控制点世界坐标。
     * @param isAuxiliaryObject 是否为辅助对象。
     * @param sourcePoint 捕捉来源点；仅记录引用关系，不表示拓扑共点。
     * @returns 草图控制点对象。
     */
    protected createLineControlPoint(
        position: THREE.Vector3,
        isAuxiliaryObject = false,
        sourcePoint: SketchPointObject | null = null,
    ): SketchPointObject {
        return new SketchPointObject({
            position: position.clone(),
            pointKind: SketchPointKind.ControlPoint,
            material: PointsMaterialFactory.getSketchControlPointSpriteMaterial(),
            referencePlane: this.referencePlane,

            sourcePoint,


            isAuxiliaryObject,

        });
    }

    /**
     * 创建草图线材质。
     * 说明：使用 LineMaterialFactory 统一管理线材质配置，确保绘制预览与渲染视图视觉一致。
     * @param isConstruction 是否构造几何。
     * @param isCenterline 是否中心线（当前按构造线处理）。
     * @returns 线材质。
     */
    protected createSketchLineMaterial(
        isConstruction: boolean,
        isCenterline = false,
    ): SketchLineMaterial {
        if (isCenterline) {
            return LineMaterialFactory.createConstructionLineMaterial();
        }
        if (isConstruction) {
            return LineMaterialFactory.createConstructionLineMaterial();
        }
        return LineMaterialFactory.getBasicMaterialByState(LineMaterialState.Default)
    }


    /**
     * 尝试基于种子边收口混合闭合集合。
     * 说明：
     * 1. 仅当闭合链至少包含两条边时才创建 `SketchCollectionObject`；
     * 2. 单条样条自闭合不走集合收口，而是由样条对象自身管理轮廓；
     * 3. 共享端点会在集合创建时一并补挂，保持后续拖拽与选择语义一致。
     * @param seedSources 新创建的种子边链。
     * @returns 成功创建的闭合集合对象；无法闭合时返回 null。
     */
    protected tryFinalizeMixedClosedCollection(
        seedSources: readonly CollectSource[],
    ): SketchCollectionObject | null {
        if (seedSources.length < 1) {
            return null;
        }

        const activeSketchGroup = this.viewer.sketchGroupManager?.activeSketchGroup;
        if (!activeSketchGroup) {
            return null;
        }

        const collectionObject = activeSketchGroup.tryFinalizeClosedSketchCollection(
            seedSources,
            {syncContour: this.viewer.shadedSketchContoursEnabled},
        );
        if (!collectionObject) {
            return null;
        }

        this.sketchDrawingManager.notifySketchEntityCreated(collectionObject);
        this.viewer.syncShadedContoursAfterSketchEntityCreated(activeSketchGroup);
        return collectionObject;
    }


    /**
     * 取走本次绘制积累的领域补丁（取出即清空）。
     *
     * 说明：绘制的几何变更由运行时快照表达，但像"槽口自动添加尺寸"这类领域数据
     * 需要以补丁形式并入**同一笔**事务，否则会变成两步撤销。
     *
     * @returns 领域补丁列表。
     */
    public takePendingHistoryPatches(): readonly EntityPatch[] {
        if (this.pendingHistoryPatches.length === 0) {
            return [];
        }

        const patches = this.pendingHistoryPatches;

        this.pendingHistoryPatches = [];

        return patches;
    }

     isAutoGeometryRelationsEnabled(): boolean {
        // 选项容器缺省（尚未初始化的宿主）按开启处理，与 snapSettings 缺省语义一致。
        return this.viewer.options?.snapSettings?.isAutoGeometryRelationsEnabled ?? true;
    }
}

